import type { CredentialCard } from '@/types';

const ROLE_LABELS: Record<string, string> = {
  hacker: 'HACKER',
  staff: 'STAFF',
  sponsor: 'SPONSOR',
  mentor: 'MENTOR',
  juez: 'JUEZ',
  invitado: 'INVITADO',
  diseno: 'DISEÑO',
  externo: 'EXTERNO',
};

// The card is captured with html-to-image and printed, so it carries its own @font-face
// rules rather than relying on the app stylesheet's family names.
const FONT_FACES = `
@font-face {
  font-family: 'Aeonik26';
  src: url('/fonts/Aeonik-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: 'Aeonik26';
  src: url('/fonts/Aeonik-Bold.ttf') format('truetype');
  font-weight: 700;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: 'AeonikMono26';
  src: url('/fonts/AeonikMono-Regular.otf') format('opentype');
  font-weight: 400;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: 'Oxanium26';
  src: url('/fonts/Oxanium-Regular.woff2') format('woff2'),
       url('/fonts/Oxanium-Regular.ttf') format('truetype');
  font-weight: 400;
  font-style: normal;
  font-display: block;
}
@font-face {
  font-family: 'Thermochrome26';
  src: url('/fonts/MDThermochrome0.3-Regular-Trial.otf') format('opentype');
  font-weight: 400;
  font-style: normal;
  font-display: block;
}
`;

interface Hack26CredentialCardProps {
  card: CredentialCard;
  dateLabel: string;
  cityLabel: string;
  sponsorsAsset: string;
  footMarginTop: number | null;
  rounded?: boolean;
}

export function Hack26CredentialCard({
  card,
  dateLabel,
  cityLabel,
  sponsorsAsset,
  footMarginTop,
  rounded = true,
}: Hack26CredentialCardProps) {
  const isHacker = card.type === 'hacker';

  const bgColor = isHacker ? '#ffffff' : '#0a0a0a';
  const fgColor = isHacker ? '#0a0a0a' : '#ffffff';
  const pillBg = isHacker ? '#0a0a0a' : '#ffffff';
  const pillFg = isHacker ? '#ffffff' : '#0a0a0a';

  const whiteAssetFilter = isHacker ? 'invert(1)' : undefined;
  const roleLabel = card.roleLabel ?? ROLE_LABELS[card.type];

  return (
    <div
      className="relative flex flex-col overflow-hidden"
      style={{
        width: '535px',
        height: '847px',
        borderRadius: rounded ? '30px' : undefined,
        backgroundColor: bgColor,
        color: fgColor,
        padding: '32px',
        fontFamily: 'Aeonik26, sans-serif',
      }}
    >
      <style>{FONT_FACES}</style>

      <div className="flex w-full items-center justify-between">
        <p
          className="text-xl"
          style={{ fontFamily: 'Aeonik26', letterSpacing: '0.08em' }}
        >
          {dateLabel}
        </p>
        <p
          className="lowercase"
          style={{
            fontFamily: 'Oxanium26',
            fontSize: '30px',
            letterSpacing: '-0.02em',
          }}
        >
          platanus hack [26]
        </p>
      </div>

      <div className="mt-6 flex justify-center">
        <span
          style={{
            display: 'inline-block',
            backgroundColor: pillBg,
            color: pillFg,
            padding: '12px 24px',
            fontFamily: 'Thermochrome26',
            fontSize: '96px',
            letterSpacing: '0.02em',
            lineHeight: 1,
          }}
        >
          {roleLabel}
        </span>
      </div>

      <div className="mt-6 flex flex-1 flex-col items-center justify-center gap-1 px-2">
        {card.fullName && (
          <p
            className="text-center uppercase"
            style={{
              fontFamily: 'AeonikMono26',
              fontSize: card.fullName.length > 18 ? '32px' : '44px',
              lineHeight: 1.05,
              letterSpacing: '-0.01em',
            }}
          >
            {card.fullName}
          </p>
        )}

        {card.company && !isHacker && (
          <p
            style={{
              fontFamily: 'Aeonik26',
              fontSize: '22px',
              letterSpacing: '0.04em',
            }}
          >
            {card.company}
          </p>
        )}

        <div style={{ marginTop: '6px' }}>
          <img
            src={card.qrDataUrl}
            alt={`QR ${card.fullName || roleLabel}`}
            width={180}
            height={180}
            className="h-[180px] w-[180px]"
            style={{ imageRendering: 'crisp-edges' }}
          />
        </div>

        {card.githubUsername && (
          <p
            className="uppercase"
            style={{ fontFamily: 'AeonikMono26', fontSize: '28px' }}
          >
            @{card.githubUsername}
          </p>
        )}
      </div>

      <div
        className="flex w-full flex-col items-center px-4"
        style={
          footMarginTop === null ? undefined : { marginTop: `${footMarginTop}px` }
        }
      >
        <p
          className="text-center"
          style={{
            fontFamily: 'Aeonik26',
            fontSize: '18px',
            letterSpacing: '0.16em',
            marginBottom: '12px',
          }}
        >
          {cityLabel}
        </p>
        <img
          src="/hack-26/credentials/logo-hack-26-white.svg"
          alt="Platanus Hack 26"
          width={420}
          height={76}
          className="h-auto w-full"
          style={whiteAssetFilter ? { filter: whiteAssetFilter } : undefined}
        />
        <img
          src={`/${sponsorsAsset}`}
          alt="sponsors"
          width={420}
          height={56}
          className="mt-8 h-auto w-full"
          style={whiteAssetFilter ? { filter: whiteAssetFilter } : undefined}
        />
      </div>
    </div>
  );
}
